import React, { useMemo, useState, useRef } from "react";
import {
  useForm,
  QuestionField,
  HealthSection,
  MedicationBlock,
  VaccinationBlock,
  DietBlock,
  styles,
  isMedSectionHeading,
  isPetNameQ,
  petKey,
  MAX_PETS_CONST,
  isHealthSection,
  isVaccinationSection,
  isAppearanceSection,
  isDietSection,
  isMedicalYNQ,
  isMedicalConditionsQ,
  isCustomMedCondQ,
  isOnMedicationQ,
  isMealTypeQ,
  isMealsPerDayQ,
  isWhatToFeedQ,
  isWhenToFeedQ,
  isHowMuchToFeedQ,
  isFeedingInstructionsQ,
  isDobQ,
  isAgeQ,
  formatAge,
  parseAge,
  isBehaviourSection,
} from "./Forms";

/* Fixed pet switcher — rendered inside the sticky header in Forms.jsx <Shell/> */
export function PetTabs() {
  const {
    petQuestions,
    petCount,
    activePet,
    setActivePet,
    addPet,
    answers,
    errors,
  } = useForm();
  const nameQ = petQuestions.find(isPetNameQ);
  const labelOf = (i) =>
    String((nameQ && answers[petKey(nameQ._id, i)]) || "").trim() ||
    `Pet ${i + 1}`;
  const hasErr = (i) => Object.keys(errors).some((k) => k.endsWith(`__${i}`));

  return (
    <div className="ff-pets">
      <div className="ff-pet-scroll" style={{ alignItems: "center" }}>
        {Array.from({ length: petCount }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActivePet(i)}
            className={`ff-pet-tab${i === activePet ? " is-on" : ""}${
              hasErr(i) ? " has-error" : ""
            }`}
          >
            <span className="ff-pet-avatar">
              {labelOf(i).charAt(0).toUpperCase()}
            </span>
            <span className="ff-pet-name">{labelOf(i)}</span>
          </button>
        ))}

        {/* + Add pet — sits inline, at the end of the row */}
        <button
          type="button"
          className="ff-add-pet"
          onClick={addPet}
          disabled={petCount >= MAX_PETS_CONST}
          style={{ flexShrink: 0, alignSelf: "center", marginTop: -18 }}
        >
          + Add pet
        </button>
      </div>
    </div>
  );
}

function AgeField({ q, fieldKey, value, error, onChange }) {
  const { years, months } = parseAge(value);
  const clean = (s, max) => {
    const d = String(s).replace(/\D/g, "").slice(0, 2);
    return d !== "" && Number(d) > max ? String(max) : d;
  };
  const input = {
    ...styles.input,
    paddingRight: 64,
    borderColor: error ? "#EF4444" : "#D1D5DB",
  };
  const unit = {
    position: "absolute",
    right: 14,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 13,
    color: "#6B7280",
    pointerEvents: "none",
  };
  return (
    <div id={`q-${fieldKey}`} className="ff-field" style={styles.field}>
      <label style={styles.label}>
        {q.question}{" "}
        {q.isRequired && <span style={{ color: "#EF4444" }}>*</span>}
      </label>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="0"
            aria-label="Years"
            value={years}
            onChange={(e) =>
              onChange(formatAge(clean(e.target.value, 40), months))
            }
            style={input}
          />
          <span style={unit}>years</span>
        </div>
        <div style={{ position: "relative", flex: 1 }}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="0"
            aria-label="Months"
            value={months}
            onChange={(e) =>
              onChange(formatAge(years, clean(e.target.value, 11)))
            }
            style={input}
          />
          <span style={unit}>months</span>
        </div>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: "#EF4444", marginTop: 6 }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default function PetDetails() {
  const {
    petQuestions,
    answers,
    errors,
    setAnswer,
    setRawAnswer,
    applyLocation,
    goBack,
    goContinue,
    petCount,
    removePet,
    petCtxFor,
    activePet,
    serviceCategories: cats,
    isLastStep,
    handleSubmit,
    submitting,
    existingSubmissionId,
  } = useForm();
  const idx = Math.min(activePet, petCount - 1);
    const [ageMode, setAgeMode] = useState({});
  const medClearRef = useRef(null);
  const dietClearRef = useRef(null);

  /* Group into heading-led sections */
  const sections = useMemo(() => {
    const out = [];
    let cur = [];
    petQuestions.forEach((q) => {
      if (q.answerType === "section_heading") {
        if (cur.length) out.push(cur);
        cur = [q];
      } else cur.push(q);
    });
    if (cur.length) out.push(cur);

    // merge consecutive sections with identical heading text
    const merged = [];
    let last = "";
    out.forEach((s) => {
      const t = (s[0]?.question || "").trim().toLowerCase();
      if (t && t === last) {
        merged[merged.length - 1] = [
          ...merged[merged.length - 1],
          ...s.slice(1),
        ];
        return;
      }
      last = t;
      merged.push(s);
    });
    return merged;
  }, [petQuestions]);

  /* Filter sections by selected service categories */
    const visibleSections = useMemo(() => {
      const catFor = (svc) => {
        const s = String(svc || "").toLowerCase();
        if (/groom/.test(s)) return "grooming";
        if (/board|day\s*care|daycare/.test(s)) return "boarding";
        if (/veterin/.test(s)) return "vet";
        if (/other/.test(s)) return "other";
        return null;
      };

      return sections.filter((s) => {
        const head = s[0];
        if (!head) return false;

        const assoc = Array.isArray(head.associated_services)
          ? head.associated_services
          : [];
        if (assoc.length > 0) {
          return assoc.some((svc) => {
            const c = catFor(svc);
            return c ? !!cats[c] : false;
          });
        }

        /* Legacy hardcoded fallback when the schema didn't tag the section */
        if (isVaccinationSection(head)) return cats.boarding || cats.vet;
        if (isAppearanceSection(head)) return cats.grooming;
        if (isBehaviourSection(head)) return cats.grooming;
        if (isMedSectionHeading(head)) return cats.boarding || cats.vet;
        if (isDietSection(head)) return cats.boarding;
        return true;
      });
    }, [sections, cats]);

  const hasMedSection = visibleSections.some((s) => isMedSectionHeading(s[0]));

  const cleanBody = (heading, body) => {
    const h = (heading?.question || "").trim().toLowerCase();
    return body.filter(
      (q) =>
        q.answerType !== "description_text" ||
        (q.question || "").trim().toLowerCase() !== h,
    );
  };

    const Heading = ({ text, onClear, showClear }) => (
      <div
        className="ff-full"
        style={{
          ...styles.sectionHeading,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h2 style={styles.sectionHeadingText}>{text}</h2>
        {showClear && onClear && (
          <button
            type="button"
            onClick={onClear}
            style={styles.sectionClearBtnInline}
          >
            Clear section
          </button>
        )}
      </div>
    );

       const renderVaccination = (section) => {
         /* First-time visitor: no prior submission → show the vaccination
         questions as normal fields, no repeater, no "+ Add another". */
         if (!existingSubmissionId) {
           return renderDefault(section);
         }

         /* Returning customer: show existing rows (locked) + add button. */
         const heading = section[0];
         const bodyQs = cleanBody(heading, section.slice(1)).filter(
           (q) =>
             q.answerType !== "section_heading" &&
             q.answerType !== "description_text",
         );
         return (
           <>
             <Heading text={heading.question} />
             <VaccinationBlock
               baseIdx={idx}
               fieldQs={bodyQs}
               answers={answers}
               errors={errors}
               setAnswer={setAnswer}
               setRawAnswer={setRawAnswer}
               petCtxFor={petCtxFor}
             />
           </>
         );
       };
   const renderDefault = (section) => {
     const heading = section[0];
     const bodyQs = cleanBody(heading, section.slice(1));
     const hasDob = bodyQs.some(isDobQ);

     return (
       <>
         <Heading text={heading.question} />
         {bodyQs.map((q) => {
           if (isDobQ(q)) {
             const ageQ = bodyQs.find(isAgeQ);
             const dobKey = petKey(q._id, idx);
             const ageKey = ageQ ? petKey(ageQ._id, idx) : null;
             const dobVal = answers[dobKey];
             const ageVal = ageKey ? answers[ageKey] : "";
             const usingAge = ageQ
               ? ageMode[idx] !== undefined
                 ? ageMode[idx]
                 : !dobVal && !!ageVal
               : false;

             return (
               <div key={dobKey} className="ff-full">
                 {usingAge ? (
                   <AgeField
                     q={ageQ}
                     fieldKey={ageKey}
                     value={ageVal}
                     error={errors[ageKey] || errors[dobKey]}
                     onChange={(v) => setAnswer(ageQ, ageKey, v, idx)}
                   />
                 ) : (
                   <>
                     <QuestionField
                       q={q}
                       fieldKey={dobKey}
                       value={dobVal}
                       error={errors[dobKey]}
                       onChange={(v) => setAnswer(q, dobKey, v, idx)}
                       petCtx={petCtxFor(idx)}
                     />
                     {dobVal && ageVal && (
                       <div style={styles.hintGreen}>✓ Age: {ageVal}</div>
                     )}
                   </>
                 )}

                 {ageQ && (
                   <button
                     type="button"
                     onClick={() => {
                       const next = !usingAge;
                       setAgeMode((m) => ({ ...m, [idx]: next }));
                       if (next) setRawAnswer(dobKey, "");
                       else setRawAnswer(ageKey, "");
                     }}
                     style={{
                       background: "none",
                       border: 0,
                       color: "#EA580C",
                       fontSize: 13,
                       fontWeight: 600,
                       textDecoration: "underline",
                       cursor: "pointer",
                       padding: 0,
                       marginTop: 8,
                       textAlign: "left",
                     }}
                   >
                     {usingAge
                       ? "Have a DOB? Use date of birth instead"
                       : "Don't remember DOB? Enter age instead"}
                   </button>
                 )}
               </div>
             );
           }

           if (isAgeQ(q)) {
             if (hasDob) return null;
             const ageKey = petKey(q._id, idx);
             return (
               <AgeField
                 key={ageKey}
                 q={q}
                 fieldKey={ageKey}
                 value={answers[ageKey]}
                 error={errors[ageKey]}
                 onChange={(v) => setAnswer(q, ageKey, v, idx)}
               />
             );
           }

           const key = petKey(q._id, idx);
           return (
             <QuestionField
               key={key}
               q={q}
               fieldKey={key}
               value={answers[key]}
               error={errors[key]}
               onChange={(v) => setAnswer(q, key, v, idx)}
               onLocate={(r) => applyLocation(r, key)}
               petCtx={petCtxFor(idx)}
             />
           );
         })}
       </>
     );
   };

  const renderHealth = (section) => {
    const heading = section[0];
    const bodyQs = cleanBody(heading, section.slice(1));
    const ynQ = bodyQs.find(isMedicalYNQ); // may be undefined
    const condQ = bodyQs.find(isMedicalConditionsQ);
    const customQ = bodyQs.find(isCustomMedCondQ);
    const rest = bodyQs.filter(
      (q) => q !== ynQ && q !== condQ && q !== customQ,
    );

    // If neither the gate nor the conditions list survived the enabled filter,
    // just fall back to the default renderer so `rest` still gets shown.
    if (!ynQ && !condQ && !customQ) return renderDefault(section);

    return (
      <>
        <Heading text={heading.question} />

        {/* Always mount — HealthSection now handles a missing ynQ itself */}
        <HealthSection
          baseIdx={idx}
          ynQ={ynQ}
          condQ={condQ}
          customQ={customQ}
          answers={answers}
          errors={errors}
          setAnswer={setAnswer}
          petCtxFor={petCtxFor}
        />

        {rest.map((q) => {
          const key = petKey(q._id, idx);
          return (
            <QuestionField
              key={key}
              q={q}
              fieldKey={key}
              value={answers[key]}
              error={errors[key]}
              onChange={(v) => setAnswer(q, key, v, idx)}
              onLocate={(r) => applyLocation(r, key)}
              petCtx={petCtxFor(idx)}
            />
          );
        })}
      </>
    );
  };

    const renderMedication = (section) => {
      const heading = section[0];
      const bodyQs = cleanBody(heading, section.slice(1));
      const ynQ = bodyQs.find(isOnMedicationQ);
      const fieldQs = bodyQs.filter((q) => q !== ynQ);
      if (!ynQ && fieldQs.length === 0) return null;

      const ynKey = ynQ ? petKey(ynQ._id, idx) : null;
      const ynVal = ynKey ? answers[ynKey] : "";
      const itemsKey = `meds__${idx}__items`;
      const items = Array.isArray(answers[itemsKey]) ? answers[itemsKey] : [];
      const hasAnyData =
        (ynQ && ynVal !== "" && ynVal != null) ||
        items.some(
          (m) =>
            m &&
            (m.name ||
              m.dosage ||
              m.instructions ||
              (Array.isArray(m.when) ? m.when.length > 0 : !!m.when) ||
              (Array.isArray(m.photos) && m.photos.length > 0)),
        );

      return (
        <>
          <Heading
            text={heading.question}
            showClear={hasAnyData}
            onClear={() => medClearRef.current?.()}
          />
          <MedicationBlock
            baseIdx={idx}
            ynQ={ynQ}
            fieldQs={fieldQs}
            answers={answers}
            errors={errors}
            setAnswer={setAnswer}
            setRawAnswer={setRawAnswer}
            petCtxFor={petCtxFor}
            registerClear={(fn) => {
              medClearRef.current = fn;
            }}
          />
        </>
      );
    };

    const renderDiet = (section) => {
      const heading = section[0];
      const bodyQs = cleanBody(heading, section.slice(1));
      const topQs = bodyQs.filter((q) => isMealTypeQ(q) || isMealsPerDayQ(q));
      const fieldQs = bodyQs.filter(
        (q) =>
          isWhatToFeedQ(q) ||
          isWhenToFeedQ(q) ||
          isHowMuchToFeedQ(q) ||
          isFeedingInstructionsQ(q),
      );
      const others = bodyQs.filter(
        (q) => !topQs.includes(q) && !fieldQs.includes(q),
      );

      const hasTopData = topQs.some((q) => {
        const v = answers[petKey(q._id, idx)];
        if (v === "" || v == null) return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      });
      const itemsKey = `diet__${idx}__items`;
      const items = Array.isArray(answers[itemsKey]) ? answers[itemsKey] : [];
      const hasItemsData = items.some(
        (m) =>
          m &&
          (m.what ||
            m.how ||
            m.instructions ||
            (Array.isArray(m.when) ? m.when.length > 0 : !!m.when)),
      );
      const hasAnyData = hasTopData || hasItemsData;

      return (
        <>
          <Heading
            text={heading.question}
            showClear={hasAnyData}
            onClear={() => dietClearRef.current?.()}
          />
          <DietBlock
            baseIdx={idx}
            topQs={topQs}
            fieldQs={fieldQs}
            answers={answers}
            errors={errors}
            setAnswer={setAnswer}
            setRawAnswer={setRawAnswer}
            petCtxFor={petCtxFor}
            registerClear={(fn) => {
              dietClearRef.current = fn;
            }}
          />
          {others.map((q) => {
            const key = petKey(q._id, idx);
            return (
              <QuestionField
                key={key}
                q={q}
                fieldKey={key}
                value={answers[key]}
                error={errors[key]}
                onChange={(v) => setAnswer(q, key, v, idx)}
                petCtx={petCtxFor(idx)}
              />
            );
          })}
        </>
      );
    };

  return (
    <form onSubmit={(e) => e.preventDefault()} noValidate>
   

      <div key={idx} id={`pet-card-${idx}`} className="ff-panel">
        <div className="ff-panel-head">
          {petCount > 1 && (
            <button
              type="button"
              onClick={() => removePet(idx)}
              style={styles.removePetBtn}
            >
              Remove this pet
            </button>
          )}
        </div>

        <div className="ff-grid">
          {visibleSections.map((section, si) => {
            const heading = section[0];
            if (!heading) return null;

            if (isHealthSection(heading)) {
              return (
                <React.Fragment key={`health-${si}`}>
                  {renderHealth(section)}
                </React.Fragment>
              );
            }
            if (isMedSectionHeading(heading)) {
              return (
                <React.Fragment key={`med-${si}`}>
                  {renderMedication(section)}
                </React.Fragment>
              );
            }
                        if (isVaccinationSection(heading)) {
                          return (
                            <React.Fragment key={`vacc-${si}`}>
                              {renderVaccination(section)}
                            </React.Fragment>
                          );
                        }
            if (isDietSection(heading)) {
              return (
                <React.Fragment key={`diet-${si}`}>
                  {renderDiet(section)}
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={`sec-${si}`}>
                {renderDefault(section)}
              </React.Fragment>
            );
          })}

          {/* {!hasMedSection && (cats.boarding || cats.vet) && (
            <>
              <Heading text="Medication Details" />
              <MedicationBlock
                baseIdx={idx}
                ynQ={null}
                fieldQs={[]}
                answers={answers}
                errors={errors}
                setAnswer={setAnswer}
                setRawAnswer={setRawAnswer}
                petCtxFor={petCtxFor}
              />
            </>
          )} */}
        </div>
      </div>

      <div className="ff-nav">
        <div className="ff-nav-inner">
          <button type="button" onClick={goBack} style={styles.secondaryBtn}>
            Back
          </button>
          <button
            type="button"
            onClick={isLastStep ? handleSubmit : goContinue}
            disabled={submitting}
            style={{
              ...styles.primaryBtn,
              flex: 1,
              marginTop: 0,
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? "progress" : "pointer",
            }}
          >
            {submitting ? "Submitting…" : isLastStep ? "Submit" : "Continue"}
          </button>
        </div>
      </div>
    </form>
  );
}
